import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  Box,
  Text,
  FormControl,
  Flex,
  ModalOverlay,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Tabs,
  Heading,
  Collapse,
  Spinner,
  useDisclosure,
} from '@chakra-ui/react';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { cottonTcx } from '@texas/resources/colors/tcx';
import { paperTpg } from '@texas/resources/colors/tpg';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorBox } from './ColorBox';
import { useRegexSearch } from '@texas/components/shared/hooks/useRegexSearch';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ColorData } from '@texas/api/endpoints/compositionApi';
import { rudholmMatrixColors } from '@texas/resources/colors/rudholmColors';
import { CustomColorPicker } from './CustomColorPicker';
import { RecommendedColors } from './RecommendedColors';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { ColorGrid } from './ColorGrid';
import { ColorsApi, ColorType } from '@texas/api/endpoints/metadata/colorsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '../alert/ErrorDetails';
import { VerifyDialogWithFunction } from '../dialog/VerifyDialogWithFunction';
import { SimpleNote } from '../SimpleNote';

export interface Color {
  code: string;
  name: string;
  hex: string | null;
  description?: string | null;
}

export interface ColorWithType extends Color {
  colorType: ColorType;
}

interface Props extends SharedDisclosureProps {
  onSelect: (colors: ColorData[]) => void;
  addedColors: ColorData[];
  matrixNodeValue?: string;
  revertReadyWarning: boolean;
  isReady: boolean;
}

export function ColorPicker({
  onSelect,
  matrixNodeValue,
  isOpen,
  onClose,
  addedColors,
  revertReadyWarning,
  isReady,
}: Props) {
  return (
    <Modal
      size="6xl"
      onClose={onClose}
      isOpen={isOpen}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <InnerContent
        matrixNodeValue={matrixNodeValue}
        onSelect={onSelect}
        addedColors={addedColors}
        revertReadyWarning={revertReadyWarning}
        isReady={isReady}
      />
    </Modal>
  );
}

function InnerContent({
  addedColors,
  matrixNodeValue,
  onSelect,
  revertReadyWarning,
  isReady,
}: {
  addedColors: ColorData[];
  matrixNodeValue?: string;
  onSelect: (colors: ColorData[]) => void;
  revertReadyWarning: boolean;
  isReady: boolean;
}) {
  const { t } = useTranslation();
  const { search, setSearch, regexSearch } = useRegexSearch();
  const [pickedColors, setPickedColors] = useState<ColorData[]>(addedColors);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const {
    data: metalShades,
    refetch,
    loading,
    error,
  } = useApiResource(ColorsApi.getAll);

  useEffect(() => {
    refetch({ colorType: ColorType.MetalShade });
  }, [refetch]);

  return (
    <ModalContent>
      <ModalCloseButton />
      <Flex>
        <ModalBody
          bg="texas.bg.800"
          _light={{ bg: 'gray.50' }}
          flex={2}
          borderRadius="md"
        >
          <Heading textTransform="none" fontSize="3xl">
            {t('colors.colorPicker')}
          </Heading>
          <FormControl pb={2}>
            <SearchFilterInput
              debounceDelay={0}
              autoFocus={true}
              placeholder={t('colors.searchColor')}
              value={search}
              onChange={(s) => setSearch(s)}
            />
          </FormControl>
          <Tabs variant="composition" defaultIndex={matrixNodeValue ? 2 : 0}>
            <TabList gap={2}>
              <Tab>{t('colors.tcx')}</Tab>
              <Tab>{t('colors.tpg')}</Tab>
              <Tab>{t('colors.rudholmColors')}</Tab>
              <Tab>{t('colors.metalShades')}</Tab>
              <Tab>{t('colors.customColor')}</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <ColorGrid
                  columns={4}
                  searchTerm={regexSearch}
                  colors={cottonTcx.colors.sort((a, b) =>
                    a.code.localeCompare(b.code),
                  )}
                  pickedColors={pickedColors.map((x) => x.code)}
                  colorPicked={(code, hex, name) => {
                    if (pickedColors.some((x) => x.code === code)) {
                      setPickedColors((s) => s.filter((p) => p.code !== code));
                      return;
                    }
                    setPickedColors((s) => [
                      ...s,
                      {
                        colorType: ColorType.PantoneTcx,
                        description: '',
                        code,
                        hex,
                        name,
                      },
                    ]);
                  }}
                />
              </TabPanel>
              <TabPanel>
                <ColorGrid
                  columns={4}
                  searchTerm={regexSearch}
                  colors={paperTpg.colors.sort((a, b) =>
                    a.code.localeCompare(b.code),
                  )}
                  pickedColors={pickedColors.map((x) => x.code)}
                  colorPicked={(code, hex, name) => {
                    if (pickedColors.some((x) => x.code === code)) {
                      setPickedColors((s) => s.filter((p) => p.code !== code));
                      return;
                    }
                    setPickedColors((s) => [
                      ...s,
                      {
                        colorType: ColorType.PantoneTpg,
                        description: '',
                        code,
                        hex,
                        name,
                      },
                    ]);
                  }}
                />
              </TabPanel>
              <TabPanel>
                <ColorGrid
                  columns={5}
                  searchTerm={regexSearch}
                  colors={rudholmMatrixColors.colors}
                  pickedColors={pickedColors.map((x) => x.code)}
                  colorPicked={(code, hex, name, desc) => {
                    if (pickedColors.some((x) => x.code === code)) {
                      setPickedColors((s) => s.filter((p) => p.code !== code));
                      return;
                    }
                    setPickedColors((s) => [
                      ...s,
                      {
                        colorType: ColorType.RudholmMatrix,
                        description: desc ?? '',
                        code,
                        hex,
                        name,
                      },
                    ]);
                  }}
                />
              </TabPanel>
              <TabPanel>
                {error && <ErrorDetails error={error} />}
                {loading && (
                  <Flex gap={2}>
                    <Text>{t('general.loadingData')}</Text>
                    <Spinner />
                  </Flex>
                )}
                {!loading && metalShades && (
                  <ColorGrid
                    columns={2}
                    searchTerm={regexSearch}
                    colors={metalShades}
                    pickedColors={pickedColors.map((x) => x.code)}
                    colorPicked={(code, hex, name) => {
                      if (pickedColors.some((x) => x.code === code)) {
                        setPickedColors((s) =>
                          s.filter((p) => p.code !== code),
                        );
                        return;
                      }
                      setPickedColors((s) => [
                        ...s,
                        {
                          colorType: ColorType.MetalShade,
                          description: '',
                          code,
                          hex,
                          name,
                        },
                      ]);
                    }}
                  />
                )}
              </TabPanel>
              <TabPanel>
                <CustomColorPicker
                  onAdd={(c) => setPickedColors((p) => [...p, c])}
                  pickedColors={pickedColors}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalBody as={Flex} flexDir="column" justifyContent="end" w="30%">
          <Box mb="auto" mt={12}>
            <RecommendedColors
              matrixNodeValue={matrixNodeValue}
              pickedColors={pickedColors}
              metalShades={metalShades ?? []}
              colorPicked={(color) => setPickedColors((s) => [...s, color])}
            />
          </Box>
          <Flex flexDir="column" gap={2}>
            {pickedColors.length === 0 ? (
              <Text variant="sub">{t('colors.noSelected')}</Text>
            ) : (
              <Heading fontSize="md">{t('colors.picked')}</Heading>
            )}
            <Collapse
              in={pickedColors
                .filter(
                  (c) =>
                    ensureEnumNumber(ColorType, c.colorType) !==
                    ColorType.MetalShade,
                )
                .some((x) => x.hex?.length === 0)}
            >
              <Text color="gray.300" fontSize="sm">
                {t('colors.colorRepresentationDescription')}
              </Text>
            </Collapse>
            {pickedColors.map((p) => {
              return (
                <ColorBox
                  key={p.code}
                  color={p}
                  colorDescription={p.description}
                  colorName={p.name}
                  fallbackDefaultValueHex={getClosestRudholmColor(p)}
                  onColorChange={
                    ensureEnumNumber(ColorType, p.colorType) !==
                      ColorType.PantoneTcx &&
                    ensureEnumNumber(ColorType, p.colorType) !==
                      ColorType.PantoneTpg
                      ? (e) =>
                          setPickedColors((s) =>
                            s.map((x) => {
                              if (x.code === p.code) {
                                return { ...x, hex: e };
                              }
                              return x;
                            }),
                          )
                      : undefined
                  }
                  onRemove={() =>
                    setPickedColors((s) => s.filter((x) => x.code !== p.code))
                  }
                  onEditDesc={(d) =>
                    setPickedColors((s) =>
                      s.map((x) => {
                        if (x.code === p.code) {
                          return { ...x, description: d };
                        }
                        return x;
                      }),
                    )
                  }
                  onEditName={
                    ensureEnumNumber(ColorType, p.colorType) ===
                      ColorType.Custom ||
                    ensureEnumNumber(ColorType, p.colorType) ===
                      ColorType.RudholmMatrix
                      ? (n) =>
                          setPickedColors((s) =>
                            s.map((x) => {
                              if (x.code === p.code) {
                                return { ...x, name: n };
                              }
                              return x;
                            }),
                          )
                      : undefined
                  }
                />
              );
            })}
            {isReady && (
              <SimpleNote
                py={2}
                color="yellow.400"
                _light={{ color: 'orange.500' }}
              >
                <Text>{t('colors.selectAndRevertIsReady')}</Text>
              </SimpleNote>
            )}
            <Button
              minW="auto"
              onClick={() => {
                if (revertReadyWarning) {
                  onOpen();
                } else {
                  onSelect(
                    pickedColors.map((x) => ({
                      code: x.code,
                      description: x.description,
                      hex: x.hex,
                      name: x.name,
                      colorType: x.colorType,
                    })),
                  );
                }
              }}
            >
              {t('colors.selectColors')}
            </Button>
          </Flex>
        </ModalBody>
      </Flex>
      <VerifyDialogWithFunction
        headerTitle={t('colors.selectColors')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        isOpen={isOpen}
        onClose={onClose}
        toPerform={() => {
          onSelect(
            pickedColors.map((x) => ({
              code: x.code,
              description: x.description,
              hex: x.hex,
              name: x.name,
              colorType: x.colorType,
            })),
          );
          onClose();
        }}
      >
        {t('colors.selectAndRevertIsReady')}
      </VerifyDialogWithFunction>
    </ModalContent>
  );
}

function getClosestRudholmColor(color: ColorWithType) {
  if (color.colorType !== ColorType.RudholmMatrix) return undefined;

  let index = rudholmMatrixColors.colors.findIndex(
    (x) => x.code === color.code,
  );

  let iterations = 1;
  while (rudholmMatrixColors.colors[index].hex.length === 0) {
    if (!rudholmMatrixColors.colors[index].searchClosest) return undefined;

    const delta = (iterations % 2) * 2 - 1;
    index = index + iterations * delta;

    iterations++;
  }

  return rudholmMatrixColors.colors[index].hex;
}

// Since we do have saved colors in the database without a type, this function
// is needed for type lookup
export function GetColorWithType(color: Color): ColorWithType {
  const rudholmColor = rudholmMatrixColors.colors.find(
    (x) => x.code === color.code,
  );

  if (rudholmColor) {
    return {
      name: rudholmColor.name,
      code: rudholmColor.code,
      hex: rudholmColor.hex,
      colorType: ColorType.RudholmMatrix,
    };
  }

  let pantone = paperTpg.colors.find((x) => x.code === color.code);

  if (pantone) {
    return {
      name: pantone.name,
      code: pantone.code,
      hex: pantone.hex,
      colorType: ColorType.PantoneTpg,
    };
  }

  pantone = cottonTcx.colors.find((x) => x.code === color.code);

  if (pantone) {
    return {
      name: pantone.name,
      code: pantone.code,
      hex: pantone.hex,
      colorType: ColorType.PantoneTcx,
    };
  }

  return {
    ...color,
    colorType: ColorType.Custom,
  };
}
